import React, { useState } from 'react';
import { useCrud } from '../lib/useCrud';
import { formatDate } from '../lib/formatUtils';
import api from '../lib/api';
import { Autocomplete } from '../components/Autocomplete';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ClientLink } from '../components/links/EntityLinks';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, Search, Edit, Trash2, MoreVertical, Globe, Loader2, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../components/ConfirmationProvider';
import { useAuth } from '../context/AuthContext';
import { Switch } from '../components/ui/switch';
import { useLookup } from '../lib/useLookup';

const REGISTRAR_FALLBACK = ['GoDaddy', 'Namecheap', 'BigRock', 'Hostinger', 'Cloudflare', 'Google Domains', 'Other'];
const emptyForm = { domain_name: '', registrar: 'GoDaddy', client_name: '', registration_date: '', expiry_date: '', auto_renew: true, nameservers: '' };

const getDaysUntilExpiry = (date) => {
  if (!date) return 999;
  const diff = new Date(date) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

export const DomainsPage = () => {
  const { data: domains, loading, fetchData, create, update, remove } = useCrud('/domains');
  const confirm = useConfirmation();
  const { options: registrarOptions } = useLookup('domain_registrar', REGISTRAR_FALLBACK);
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('domains.create');
  const canEdit = hasPermission('domains.edit');
  const canDelete = hasPermission('domains.delete');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  // client_name → id map so the Client column can render as a real link
  // to /clients/{id}. Domain docs historically stored only client_name;
  // rather than back-fill server-side we build the map once from the
  // (already-paginated) /clients endpoint.
  const [clientMap, setClientMap] = useState({});
  React.useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/clients?limit=500');
        if (res.data?.success) {
          const m = {};
          (res.data.data || []).forEach(c => {
            const key = (c.company_name || c.name || '').trim().toLowerCase();
            if (key) m[key] = c.id;
          });
          setClientMap(m);
        }
      } catch { /* ignore — link will just fall back to plain text */ }
    })();
  }, []);
  const resolveClientId = (d) => d.client_id || clientMap[(d.client_name || '').trim().toLowerCase()] || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedDomain) {
        await update(selectedDomain.id, formData);
        toast.success('Domain updated');
      } else {
        await create(formData);
        toast.success('Domain added');
      }
      fetchData();
      setIsDialogOpen(false);
      resetForm();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Operation failed';
      toast.error(detail);
      // Keep dialog open so the user can correct the duplicate / invalid value
    }
  };

  const handleDelete = async (d) => {
    const confirmed = await confirm({ title: 'Remove Domain', description: `Are you sure you want to remove "${d.domain_name}"? This action cannot be undone.` });
    if (!confirmed) return;
    try { await remove(d.id); toast.success('Domain removed'); fetchData(); } catch { toast.error('Delete failed'); }
  };

  const handleEdit = (d) => {
    setSelectedDomain(d);
    setFormData({ domain_name: d.domain_name || '', registrar: d.registrar || 'GoDaddy', client_name: d.client_name || '', registration_date: (d.registration_date || '').split('T')[0], expiry_date: (d.expiry_date || '').split('T')[0], auto_renew: d.auto_renew ?? true, nameservers: d.nameservers || '' });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const resetForm = () => { setFormData(emptyForm); setSelectedDomain(null); setIsEditing(false); };

  const getExpiryDisplay = (date) => {
    // Two-line display matching the Hosting page: absolute date on top,
    // relative "in N days" / "expired N days ago" underneath, colour-coded.
    if (!date) return <span className="text-muted-foreground text-xs">—</span>;
    const days = getDaysUntilExpiry(date);
    let relCls = 'text-muted-foreground';
    let relText = `in ${days} days`;
    if (days < 0) { relCls = 'text-red-500'; relText = `expired ${Math.abs(days)} days ago`; }
    else if (days <= 30) { relCls = 'text-amber-500'; relText = `in ${days} days`; }
    else if (days <= 90) { relCls = 'text-amber-500/80'; }
    else relCls = 'text-emerald-500';
    return (
      <div className="leading-tight">
        <div className="text-sm font-medium">{formatDate(date)}</div>
        <div className={`text-[11px] ${relCls}`}>{relText}</div>
      </div>
    );
  };

  const filtered = domains.filter(d => !searchQuery || d.domain_name?.toLowerCase().includes(searchQuery.toLowerCase()) || d.client_name?.toLowerCase().includes(searchQuery.toLowerCase()));
  // Match the sort order used on Hosting & AMC pages: earliest expiry
  // first (already-expired rows float to the top, then upcoming
  // renewals). Domains inherit useCrud (which sorts by created_at desc
  // server-side) so we re-sort client-side here.
  const sorted = [...filtered].sort((a, b) => {
    const av = a.expiry_date ? new Date(a.expiry_date).getTime() : Infinity;
    const bv = b.expiry_date ? new Date(b.expiry_date).getTime() : Infinity;
    return av - bv;
  });
  const expiringCount = sorted.filter(d => getDaysUntilExpiry(d.expiry_date) <= 30).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="domains-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="text-2xl font-bold">Domains</h1><p className="text-muted-foreground">Track and manage domain registrations</p></div>
        <Dialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) resetForm(); }}>
          {canCreate && (<DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-domain-btn"><Plus className="h-4 w-4 mr-2" />Add Domain</Button></DialogTrigger>)}
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{isEditing ? 'Edit Domain' : 'Add Domain'}</DialogTitle><DialogDescription>Enter domain details</DialogDescription></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Client <span className="text-red-500">*</span></Label>
                <Autocomplete
                  value={formData.client_name}
                  onChange={v => setFormData({ ...formData, client_name: v })}
                  fetchOptions={async (q) => { const res = await api.get(`/clients?search=${q}&limit=10`); return res.data.success ? res.data.data.map(c => c.company_name || c.name) : []; }}
                  placeholder="Search clients..."
                  data-testid="domain-client"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2"><Label>Domain Name *</Label><Input value={formData.domain_name} onChange={e => setFormData({ ...formData, domain_name: e.target.value })} placeholder="example.com" required data-testid="domain-name-input" /></div>
                <div className="space-y-2 col-span-2"><Label>Registrar</Label><Select value={formData.registrar} onValueChange={v => setFormData({ ...formData, registrar: v })}><SelectTrigger data-testid="domain-registrar"><SelectValue /></SelectTrigger><SelectContent>{registrarOptions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Registered</Label><Input type="date" value={formData.registration_date} onChange={e => setFormData({ ...formData, registration_date: e.target.value })} /></div>
                <div className="space-y-2"><Label>Expires *</Label><Input type="date" value={formData.expiry_date} onChange={e => setFormData({ ...formData, expiry_date: e.target.value })} required data-testid="domain-expiry" /></div>
              </div>
              <div className="flex items-center justify-between p-3 rounded-md border border-border bg-muted/20">
                <div className="space-y-0.5">
                  <Label>Auto-renew</Label>
                  <p className="text-xs text-muted-foreground">Will the registrar automatically renew this domain before expiry?</p>
                </div>
                <Switch checked={formData.auto_renew} onCheckedChange={(v) => setFormData({ ...formData, auto_renew: v })} data-testid="domain-auto-renew" />
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button><Button type="submit" data-testid="domain-submit-btn">{isEditing ? 'Update' : 'Add'}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card><CardContent className="py-4"><div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search by domain or client name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="domain-search" /></div>
      </div></CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><Globe className="h-5 w-5 text-primary" /></div><div><p className="text-2xl font-bold">{sorted.length}</p><p className="text-xs text-muted-foreground">Total Domains</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><Globe className="h-5 w-5 text-emerald-500" /></div><div><p className="text-2xl font-bold">{sorted.filter(d => d.auto_renew).length}</p><p className="text-xs text-muted-foreground">Auto-Renew</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-amber-500" /></div><div><p className="text-2xl font-bold">{expiringCount}</p><p className="text-xs text-muted-foreground">Expiring Soon</p></div></div></CardContent></Card>
      </div>

      <Card><CardContent className="p-0">
        {loading ? <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> :
        sorted.length === 0 ? <div className="flex flex-col items-center justify-center h-64 text-muted-foreground"><Globe className="h-12 w-12 mb-4 opacity-50" /><p>No domains found</p></div> :
        <Table data-testid="domains-table"><TableHeader><TableRow><TableHead>Domain</TableHead><TableHead>Client</TableHead><TableHead>Registrar</TableHead><TableHead>Expires</TableHead><TableHead>Auto-Renew</TableHead><TableHead className="w-[80px]">Actions</TableHead></TableRow></TableHeader>
        <TableBody>{sorted.map(d => (
          <TableRow key={d.id} data-testid={`domain-row-${d.id}`}>
            <TableCell><div className="flex items-center gap-2"><Globe className="h-4 w-4 text-blue-500" /><span className="font-medium">{d.domain_name}</span><a href={`https://${d.domain_name}`} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3 w-3 text-muted-foreground hover:text-primary" /></a></div></TableCell>
            <TableCell><ClientLink id={resolveClientId(d)} name={d.client_name} /></TableCell>
            <TableCell><Badge variant="outline">{d.registrar}</Badge></TableCell>
            <TableCell>{getExpiryDisplay(d.expiry_date)}</TableCell>
            <TableCell>{d.auto_renew ? <Badge className="bg-emerald-500/10 text-emerald-500">Yes</Badge> : <Badge className="bg-gray-500/10 text-gray-400">No</Badge>}</TableCell>
            <TableCell>
              <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`domain-actions-${d.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit && (<DropdownMenuItem onClick={() => handleEdit(d)} data-testid={`domain-edit-${d.id}`}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>)}
                {canDelete && (<DropdownMenuItem onClick={() => handleDelete(d)} className="text-destructive focus:text-destructive" data-testid={`domain-delete-${d.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>)}
              </DropdownMenuContent></DropdownMenu>
            </TableCell>
          </TableRow>))}</TableBody></Table>}
      </CardContent></Card>
    </div>
  );
};
